import { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { Search, Home, MapPin, Euro, Bed, Square, Building2, Calendar } from 'lucide-react';

interface Property {
  id: number;
  codice: string;  // Codice annuncio (VEN001, AFF001, etc.)
  tipo_operazione: 'vendita' | 'affitto';
  city: string;
  quartiere: string;
  type: string;
  rooms: number;
  bathrooms: number;
  square_meters: number;
  piano: number;
  condizione: string;
  classe_energetica: string;
  condominio_mensile: number;
  description: string;
  created_at: string;
  price?: number;
  price_monthly?: number;
  tipo_contratto?: string;
  note?: string;  // Note dettagliate
}

export default function PropertiesList() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filtri
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterTipoOperazione, setFilterTipoOperazione] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMinPrice, setFilterMinPrice] = useState('');
  const [filterMaxPrice, setFilterMaxPrice] = useState('');
  
  // Statistiche
  const [stats, setStats] = useState<any>(null);
  
  // Note espanse
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());

  // Carica immobili
  useEffect(() => {
    loadProperties();
    loadStats();
  }, []);

  // Applica filtri quando cambiano
  useEffect(() => {
    applyFilters();
  }, [properties, searchQuery, filterCity, filterTipoOperazione, filterType, filterMinPrice, filterMaxPrice]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/properties/list?limit=100`);
      if (!response.ok) throw new Error('Errore caricamento immobili');
      
      const data = await response.json();
      setProperties(data.properties || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
      console.error('Errore caricamento immobili:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/properties/stats/summary`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Errore caricamento statistiche:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...properties];

    // Filtro ricerca testuale (include codice annuncio)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(prop => 
        prop.city.toLowerCase().includes(query) ||
        prop.quartiere.toLowerCase().includes(query) ||
        prop.type.toLowerCase().includes(query) ||
        prop.description.toLowerCase().includes(query) ||
        (prop.codice && prop.codice.toLowerCase().includes(query))
      );
    }

    // Filtro città
    if (filterCity) {
      filtered = filtered.filter(prop => prop.city === filterCity);
    }

    // Filtro tipo operazione
    if (filterTipoOperazione) {
      filtered = filtered.filter(prop => prop.tipo_operazione === filterTipoOperazione);
    }

    // Filtro tipo immobile
    if (filterType) {
      filtered = filtered.filter(prop => prop.type === filterType);
    }

    // Filtro prezzo
    if (filterMinPrice) {
      const min = parseInt(filterMinPrice);
      filtered = filtered.filter(prop => {
        const price = prop.price || prop.price_monthly || 0;
        return price >= min;
      });
    }

    if (filterMaxPrice) {
      const max = parseInt(filterMaxPrice);
      filtered = filtered.filter(prop => {
        const price = prop.price || prop.price_monthly || 0;
        return price <= max;
      });
    }

    setFilteredProperties(filtered);
  };

  // Città uniche per filtro
  const uniqueCities = Array.from(new Set(properties.map(p => p.city))).sort();
  const uniqueTypes = Array.from(new Set(properties.map(p => p.type))).sort();

  const formatPrice = (prop: Property) => {
    if (prop.tipo_operazione === 'vendita' && prop.price) {
      return `${prop.price.toLocaleString('it-IT')}€`;
    } else if (prop.tipo_operazione === 'affitto' && prop.price_monthly) {
      return `${prop.price_monthly.toLocaleString('it-IT')}€/mese`;
    }
    return 'N/A';
  };

  const getConditionColor = (condizione: string) => {
    switch (condizione) {
      case 'nuovo': return 'bg-green-100 text-green-800';
      case 'ottimo': return 'bg-blue-100 text-blue-800';
      case 'buono': return 'bg-yellow-100 text-yellow-800';
      case 'da ristrutturare': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEnergyClassColor = (classe: string) => {
    if (classe === 'A') return 'bg-green-500 text-white';
    if (classe === 'B') return 'bg-blue-500 text-white';
    if (classe === 'C') return 'bg-yellow-500 text-white';
    if (['D', 'E'].includes(classe)) return 'bg-orange-500 text-white';
    return 'bg-red-500 text-white';
  };

  const toggleNote = (id: number) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-700">Caricamento immobili...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md">
          <h2 className="text-xl font-bold text-red-600 mb-2">❌ Errore</h2>
          <p className="text-gray-700">{error}</p>
          <button
            onClick={loadProperties}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Home className="w-8 h-8 text-blue-600" />
                Lista Immobili
              </h1>
              <p className="text-gray-600 mt-1">
                {stats && `${stats.total} immobili disponibili`}
                {stats && ` • ${stats.vendita} in vendita • ${stats.affitto} in affitto`}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">{filteredProperties.length}</div>
              <div className="text-sm text-gray-600">immobili trovati</div>
            </div>
          </div>

          {/* Barra Ricerca */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Cerca per codice (90001, 10023), città, quartiere..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Filtri */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Città</label>
              <select
                value={filterCity}
                onChange={(e) => setFilterCity(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              >
                <option value="">Tutte</option>
                {uniqueCities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Operazione</label>
              <select
                value={filterTipoOperazione}
                onChange={(e) => setFilterTipoOperazione(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              >
                <option value="">Tutte</option>
                <option value="vendita">Vendita</option>
                <option value="affitto">Affitto</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              >
                <option value="">Tutti</option>
                {uniqueTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Prezzo Min</label>
              <input
                type="number"
                placeholder="Min"
                value={filterMinPrice}
                onChange={(e) => setFilterMinPrice(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Prezzo Max</label>
              <input
                type="number"
                placeholder="Max"
                value={filterMaxPrice}
                onChange={(e) => setFilterMaxPrice(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Reset Filtri */}
          {(filterCity || filterTipoOperazione || filterType || filterMinPrice || filterMaxPrice || searchQuery) && (
            <button
              onClick={() => {
                setFilterCity('');
                setFilterTipoOperazione('');
                setFilterType('');
                setFilterMinPrice('');
                setFilterMaxPrice('');
                setSearchQuery('');
              }}
              className="mt-4 text-blue-600 hover:text-blue-800 text-sm font-semibold"
            >
              🔄 Reset filtri
            </button>
          )}
        </div>
      </div>

      {/* Lista Immobili */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {filteredProperties.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <Home className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Nessun immobile trovato</h3>
            <p className="text-gray-600">Prova a modificare i filtri di ricerca</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProperties.map((prop) => (
              <div
                key={prop.id}
                className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow overflow-hidden"
              >
                {/* Header Card con CODICE */}
                <div className={`p-4 ${prop.tipo_operazione === 'vendita' ? 'bg-blue-600' : 'bg-green-600'} text-white`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold">
                      📋 {prop.codice || 'N/A'}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${getEnergyClassColor(prop.classe_energetica)}`}>
                      Classe {prop.classe_energetica}
                    </span>
                  </div>
                  <div className="font-bold text-lg">
                    {prop.tipo_operazione === 'vendita' ? '🏠 Vendita' : '🔑 Affitto'}
                  </div>
                </div>

                {/* Contenuto */}
                <div className="p-6">
                  {/* Prezzo */}
                  <div className="mb-4">
                    <div className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                      <Euro className="w-6 h-6" />
                      {formatPrice(prop)}
                    </div>
                    {prop.tipo_operazione === 'affitto' && prop.tipo_contratto && (
                      <div className="text-sm text-gray-600 mt-1">Contratto: {prop.tipo_contratto}</div>
                    )}
                  </div>

                  {/* Localizzazione */}
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center gap-2 text-gray-700">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold">{prop.quartiere}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building2 className="w-4 h-4" />
                      <span>{prop.city}</span>
                    </div>
                  </div>

                  {/* Caratteristiche */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Bed className="w-4 h-4 text-blue-600" />
                      <span className="text-sm">{prop.rooms} locali</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Square className="w-4 h-4 text-green-600" />
                      <span className="text-sm">{prop.square_meters} m²</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {prop.bathrooms} bagno{prop.bathrooms > 1 ? 'i' : ''}
                    </div>
                    <div className="text-sm text-gray-600">
                      Piano {prop.piano}
                    </div>
                  </div>

                  {/* Condizione */}
                  <div className="mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getConditionColor(prop.condizione)}`}>
                      {prop.condizione}
                    </span>
                  </div>

                  {/* Descrizione */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {prop.description}
                  </p>

                  {/* Bottone Note */}
                  {prop.note && (
                    <div className="mb-4">
                      <button
                        onClick={() => toggleNote(prop.id)}
                        className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <span>📝</span>
                        <span>{expandedNotes.has(prop.id) ? 'Nascondi Note' : 'Mostra Note'}</span>
                        <span>{expandedNotes.has(prop.id) ? '▲' : '▼'}</span>
                      </button>
                      {expandedNotes.has(prop.id) && (
                        <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <h4 className="font-semibold text-gray-800 mb-2">📋 Note dettagliate:</h4>
                          <p className="text-sm text-gray-700 leading-relaxed">{prop.note}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Condominio */}
                  {prop.condominio_mensile > 0 && (
                    <div className="text-xs text-gray-500 border-t pt-3">
                      Condominio: {prop.condominio_mensile}€/mese
                    </div>
                  )}

                  {/* Data */}
                  <div className="text-xs text-gray-400 mt-3 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(prop.created_at).toLocaleDateString('it-IT')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
